/**
 * @file Backup JSON, envío POST y PDF (html2pdf.js).
 * @module exportImport
 */

import html2pdf from "html2pdf.js";

/** Versión del payload de backup; debe coincidir con `parseImportPayload`. */
export const exportPayloadVersion = 1;

const PROFILE_IMAGE_KEY = "profileImage";

/**
 * Arma el objeto que se serializa al archivo `.json` (sin duplicar la foto dentro de cada locale).
 * @param {{
 *   profileImage?: string,
 *   locales: { en: Record<string, unknown>, es: Record<string, unknown> }
 * }} store
 * @param {string} templateId `structure.id` activo
 * @returns {{
 *   version: number,
 *   templateId: string,
 *   exportedAt: string,
 *   profileImage: string,
 *   locales: { en: Record<string, unknown>, es: Record<string, unknown> }
 * }}
 */
export function buildExportPayload(store, templateId) {
	const stripPhoto = (o) => {
		const x = { ...(o ?? {}) };
		delete x[PROFILE_IMAGE_KEY];
		return x;
	};
	const img =
		typeof store.profileImage === "string" && store.profileImage.startsWith("data:")
			? store.profileImage
			: "";
	return {
		version: exportPayloadVersion,
		templateId,
		exportedAt: new Date().toISOString(),
		profileImage: img,
		locales: {
			en: stripPhoto(store.locales?.en),
			es: stripPhoto(store.locales?.es),
		},
	};
}

/**
 * Cuerpo para POST (`submitUrl`): `fields` del locale activo y `profileImage`.
 * @param {() => Record<string, unknown>} readData
 * @param {string} templateId
 * @param {"en" | "es"} locale
 * @returns {{
 *   version: number,
 *   templateId: string,
 *   exportedAt: string,
 *   profileImage: string,
 *   locale: string,
 *   fields: Record<string, unknown>
 * }}
 */
export function buildSubmitCvPayload(readData, templateId, locale) {
	const data = readData();
	const img =
		typeof data[PROFILE_IMAGE_KEY] === "string" &&
			data[PROFILE_IMAGE_KEY].startsWith("data:")
			? data[PROFILE_IMAGE_KEY]
			: "";
	const fields = { ...data };
	delete fields[PROFILE_IMAGE_KEY];
	return {
		version: exportPayloadVersion,
		templateId,
		exportedAt: new Date().toISOString(),
		profileImage: img,
		locale,
		fields,
	};
}

/**
 * @param {unknown} parsed
 * @returns {{
 *   templateId: string,
 *   profileImage: string,
 *   locales: { en: Record<string, unknown>, es: Record<string, unknown> }
 * } | null}
 */
export function parseImportPayload(parsed) {
	if (!parsed || typeof parsed !== "object") return null;
	const obj = parsed;
	if (obj.version !== exportPayloadVersion) return null;
	if (typeof obj.templateId !== "string" || !obj.templateId) return null;
	if (!obj.locales || typeof obj.locales !== "object") return null;

	const en = obj.locales.en;
	const es = obj.locales.es;
	const stripPhoto = (o) => {
		const x =
			typeof o === "object" && o !== null && !Array.isArray(o) ? { ...o } : {};
		delete x[PROFILE_IMAGE_KEY];
		return x;
	};
	const profileImage =
		typeof obj.profileImage === "string" && obj.profileImage.startsWith("data:")
			? obj.profileImage
			: "";
	return {
		templateId: obj.templateId,
		profileImage,
		locales: {
			en: stripPhoto(en),
			es: stripPhoto(es),
		},
	};
}

/**
 * Descarga un `.json` vía `<a download>`.
 * @param {Record<string, unknown>} payload
 * @param {string} filename
 */
export function downloadJsonFile(payload, filename) {
	const blob = new Blob([JSON.stringify(payload, null, 2)], {
		type: "application/json",
	});
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename || "cv-backup.json";
	a.click();
	URL.revokeObjectURL(url);
}

/**
 * Limita el aside de la plantilla Split al alto útil de una hoja A4 (márgenes), para evitar que html2pdf repita la franja en cada corte.
 * @param {ParentNode} scope Documento clonado o raíz del clon.
 */
function clampSplitAsideForPdf(scope) {
	scope.querySelectorAll(".cv-sheet--split").forEach((sheet) => {
		const grid = sheet.querySelector(".cv-sheet__split-grid");
		const aside = sheet.querySelector(".cv-sheet__aside");
		if (grid) {
      /** @type {HTMLElement} */ (grid).style.gridTemplateRows = "auto";
		}
		if (aside) {
			const a = /** @type {HTMLElement} */ (aside);
			a.style.alignSelf = "start";
			a.style.boxSizing = "border-box";
			a.style.minHeight = PDF_SPLIT_ASIDE_PAGE_BODY_MM;
			a.style.maxHeight = PDF_SPLIT_ASIDE_PAGE_BODY_MM;
			a.style.overflow = "hidden";
		}
	});
}

/**
 * Fija tamaño y `object-fit` de las fotos de perfil en el clon (html2canvas).
 * @param {Document} clonedDoc Documento del callback `onclone` de html2pdf.
 */
function clampCvPhotosForPdf(clonedDoc) {
	clonedDoc.querySelectorAll(".cv-sheet__photo").forEach((node) => {
		const el = /** @type {HTMLElement} */ (node);
		el.style.width = "96px";
		el.style.height = "96px";
		el.style.minWidth = "96px";
		el.style.minHeight = "96px";
		el.style.flexShrink = "0";
		el.style.boxSizing = "border-box";
		el.style.borderRadius = "50%";
		if (node instanceof HTMLImageElement) {
			el.style.display = "block";
			el.style.objectFit = "cover";
			el.style.objectPosition = "center center";
		} else {
			el.style.overflow = "hidden";
			el.style.backgroundSize = "cover";
			el.style.backgroundPosition = "center center";
			el.style.backgroundRepeat = "no-repeat";
		}
	});
}

const PDF_MARGIN_MM = 15;
const PDF_EXPORT_ROOT_WIDTH = `${210 - PDF_MARGIN_MM * 2}mm`;
/** Alto útil de página A4 menos márgenes; aside Split (evita repetición al paginar). */
const PDF_SPLIT_ASIDE_PAGE_BODY_MM = `${297 - PDF_MARGIN_MM * 2}mm`;

const PDF_HTML2CANVAS_SCALE = 4;

/**
 * Espera dos frames para que el layout del clon se estabilice antes del rasterizado.
 * @returns {Promise<void>}
 */
function waitNextPaint() {
	return new Promise((resolve) => {
		requestAnimationFrame(() => requestAnimationFrame(resolve));
	});
}

/** Bloques de texto donde html2pdf puede cortar a mitad de línea. */
const PDF_LINE_PAD_SELECTORS = [".cv-sheet__text", ".cv-sheet__tags li"];

/** Líneas partidas + títulos cortos que html2pdf debe empujar a la página siguiente. */
const PDF_PAGE_BREAK_AVOID =
	".cv-sheet__section-title, .cv-sheet__hub-title, .cv-sheet__block-head";

/**
 * Altura de corte de página en px del DOM, alineada con html2pdf (`floor(canvasW * ratio) / scale`).
 * @param {HTMLElement} root
 * @returns {number}
 */
function measurePdfSlicePageHeightPx(root) {
	const width = root.scrollWidth || root.offsetWidth;
	const ratio =
		(297 - PDF_MARGIN_MM * 2) / (210 - PDF_MARGIN_MM * 2);
	return (
		Math.floor(width * PDF_HTML2CANVAS_SCALE * ratio) / PDF_HTML2CANVAS_SCALE
	);
}

/**
 * @typedef {{ start: number, end: number, top: number, bottom: number }} PdfTextLineRange
 */

/**
 * @param {ParentNode} container
 * @param {number} charIndex
 * @returns {{ node: Text, offset: number } | null}
 */
function getTextNodeAtCharacterOffset(container, charIndex) {
	const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
	let passed = 0;
	let node;
	while ((node = walker.nextNode())) {
		const len = node.textContent?.length ?? 0;
		if (charIndex < passed + len) {
			return { node: /** @type {Text} */ (node), offset: charIndex - passed };
		}
		passed += len;
	}
	return null;
}

/**
 * Detecta líneas visuales dentro de un bloque de texto (respeta `pre-wrap` del preview).
 * @param {HTMLElement} container
 * @returns {PdfTextLineRange[]}
 */
function getPdfTextLineRangesForElement(container) {
	const text = container.textContent ?? "";
	if (!text) return [];

	const range = document.createRange();
	const lines = [];
	let lineStart = 0;
	let prevTop = null;

	for (let i = 0; i < text.length; i++) {
		const startPos = getTextNodeAtCharacterOffset(container, i);
		const endPos = getTextNodeAtCharacterOffset(container, i + 1);
		if (!startPos || !endPos) continue;

		range.setStart(startPos.node, startPos.offset);
		range.setEnd(endPos.node, endPos.offset);
		const top = Math.round(range.getBoundingClientRect().top);
		if (prevTop !== null && top !== prevTop) {
			const lineStartPos = getTextNodeAtCharacterOffset(container, lineStart);
			const lineEndPos = getTextNodeAtCharacterOffset(container, i);
			if (lineStartPos && lineEndPos) {
				range.setStart(lineStartPos.node, lineStartPos.offset);
				range.setEnd(lineEndPos.node, lineEndPos.offset);
				const rect = range.getBoundingClientRect();
				lines.push({
					start: lineStart,
					end: i,
					top: rect.top,
					bottom: rect.bottom,
				});
			}
			lineStart = i;
		}
		prevTop = top;
	}

	const lastStart = getTextNodeAtCharacterOffset(container, lineStart);
	const lastEnd = getTextNodeAtCharacterOffset(container, text.length);
	if (lastStart && lastEnd) {
		range.setStart(lastStart.node, lastStart.offset);
		range.setEnd(lastEnd.node, lastEnd.offset);
		const rect = range.getBoundingClientRect();
		lines.push({
			start: lineStart,
			end: text.length,
			top: rect.top,
			bottom: rect.bottom,
		});
	}

	return lines;
}

/**
 * @param {PdfTextLineRange} line
 * @param {number} pxPageHeight
 * @returns {boolean}
 */
function lineStraddlesPdfPage(line, pxPageHeight) {
	return (
		Math.floor(line.top / pxPageHeight) !== Math.floor(line.bottom / pxPageHeight)
	);
}

/**
 * Inserta espacio en blanco antes de una línea para alinear el corte con html2pdf.
 * @param {HTMLElement} container
 * @param {number} charIndex
 * @param {number} padHeightPx
 * @returns {boolean}
 */
function insertPdfSlicePadBefore(container, charIndex, padHeightPx) {
	if (padHeightPx <= 0) return false;

	const pos = getTextNodeAtCharacterOffset(container, charIndex);
	if (!pos) return false;

	const range = document.createRange();
	range.setStart(pos.node, pos.offset);
	range.setEnd(pos.node, pos.offset);

	const pad = container.ownerDocument.createElement("span");
	pad.className = "cv-pdf-page-pad";
	pad.setAttribute("aria-hidden", "true");
	pad.style.cssText = [
		"display:block",
		`height:${padHeightPx}px`,
		"margin:0",
		"padding:0",
		"border:0",
	].join(";");
	range.insertNode(pad);
	return true;
}

/**
 * Empuja solo las líneas que el rasterizado partiría, sin rearmar el párrafo entero.
 * @param {HTMLElement} scope Raíz del clon PDF.
 */
function padPdfStraddleLines(scope) {
	const pxPageHeight = measurePdfSlicePageHeightPx(scope);

	for (const selector of PDF_LINE_PAD_SELECTORS) {
		scope.querySelectorAll(selector).forEach((el) => {
			if (!(el instanceof HTMLElement) || el.dataset.pdfPadsApplied === "1") return;
			if (!el.textContent?.trim()) {
				el.dataset.pdfPadsApplied = "1";
				return;
			}

			for (;;) {
				const straddle = getPdfTextLineRangesForElement(el)
					.filter((line) => lineStraddlesPdfPage(line, pxPageHeight))
					.sort((a, b) => b.start - a.start)[0];
				if (!straddle) break;

				const padHeight = pxPageHeight - (straddle.top % pxPageHeight);
				if (!insertPdfSlicePadBefore(el, straddle.start, padHeight)) break;
			}

			el.dataset.pdfPadsApplied = "1";
		});
	}
}

/**
 * Clona la vista previa fuera de pantalla y genera un PDF A4 (html2pdf.js).
 * El clon fuerza `min-height: auto` en la raíz para no añadir una hoja vacía al trocear el canvas.
 * @param {HTMLElement} element Contenedor del CV (p. ej. `#cvPreviewRoot`).
 * @param {string} filename Nombre del `.pdf`.
 * @returns {Promise<void>}
 */
export async function downloadPdfFromElement(element, filename) {
	const wrapper = document.createElement("div");
	wrapper.setAttribute("aria-hidden", "true");
	wrapper.style.cssText = [
		"position:fixed",
		"left:-12000px",
		"top:0",
		`width:${PDF_EXPORT_ROOT_WIDTH}`,
		"min-height:0",
		"margin:0",
		"padding:0",
		"border:0",
		"box-sizing:border-box",
		"overflow:visible",
		"pointer-events:none",
	].join(";");

	const clone = /** @type {HTMLElement} */ (element.cloneNode(true));
	clone.removeAttribute("id");
	clone.style.minHeight = "auto";
	wrapper.append(clone);
	document.body.append(wrapper);

	const sheet = clone.querySelector(".cv-sheet");
	if (sheet) /** @type {HTMLElement} */ (sheet).style.padding = "0";
	clampSplitAsideForPdf(clone);

	void clone.offsetWidth;
	await waitNextPaint();

	const scrollW = clone.scrollWidth;
	const scrollH = clone.scrollHeight;

	const opt = {
		margin: [15, 15, 15, 15],
		filename: filename || "cv.pdf",
		image: { type: "png", quality: 1 },
		html2canvas: {
			scale: PDF_HTML2CANVAS_SCALE,
			useCORS: true,
			logging: false,
			allowTaint: true,
			windowWidth: scrollW,
			windowHeight: scrollH,
			scrollX: 0,
			scrollY: 0,
			onclone: (clonedDoc) => {
				clonedDoc.querySelectorAll(".cv-preview-root.cv-render-theme").forEach((node) => {
          /** @type {HTMLElement} */ (node).style.minHeight = "auto";
				});
				clonedDoc.querySelectorAll(".cv-render-theme .cv-sheet").forEach((node) => {
          /** @type {HTMLElement} */ (node).style.padding = "0";
				});
				clampCvPhotosForPdf(clonedDoc);
				clampSplitAsideForPdf(clonedDoc);
				const padScope =
					clonedDoc.querySelector(".html2pdf__container") ??
					clonedDoc.querySelector(".cv-preview-root") ??
					clonedDoc.body;
				padPdfStraddleLines(/** @type {HTMLElement} */ (padScope));
			},
		},
		jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
		pagebreak: { mode: [], avoid: PDF_PAGE_BREAK_AVOID },
	};

	try {
		await html2pdf().set(opt).from(clone).save();
	} finally {
		wrapper.remove();
	}
}
